import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LevelMeter,
  Select,
} from '@wundo/ui';
import { audioDeviceOptions } from '@/lib/audioDeviceOptions.ts';
import { useAudioSetup } from '@/lib/useAudioSetup.ts';
import { m } from '@/paraglide/messages.js';

export interface OfflineRecordingStartDialogProps {
  open: boolean;
  pending: boolean;
  failed: boolean;
  /** The chosen microphone; null hands the browser's own default over. */
  onStart: (deviceId: string | null) => void;
  onCancel: () => void;
}

/**
 * The question in front of every microphone recording: which microphone, does
 * it hear you, and does the other person know.
 *
 * The bar is the part that matters. Device names come from the driver, and on a
 * docked laptop three of them are equally plausible; saying something and
 * watching the bar move is the only test that answers the question — and here
 * the cost of getting it wrong is a whole conversation recorded off the closed
 * lid, found out afterwards.
 *
 * Consent is one sentence rather than a checkbox. Wundo cannot promise anybody
 * was told, so it does not ask the user to promise it either; it says the thing
 * that has to be said, where it cannot be missed.
 */
export function OfflineRecordingStartDialog({
  open,
  pending,
  failed,
  onStart,
  onCancel,
}: OfflineRecordingStartDialogProps) {
  /*
   * The microphone is only open while the question is on screen. A page holding
   * one after the dialog closes lights the recording indicator, which here
   * would say the recording has already started.
   */
  const setup = useAudioSetup(open);
  const { microphones, microphoneId, level, block, chooseMicrophone } = setup;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{m.meeting_offline_dialog_title()}</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-3.5 px-5 py-4'>
          <p className='text-sm text-ink-body'>{m.meeting_offline_dialog_consent()}</p>

          {block !== null && (
            <p role='alert' className='text-sm text-danger'>
              {m.practice_session_mic_blocked()}
            </p>
          )}

          {microphones.length > 0 && (
            <label className='flex flex-col gap-2 text-base font-semibold text-ink'>
              {m.practice_session_audio_microphone()}
              <Select
                value={microphoneId ?? ''}
                options={audioDeviceOptions(microphones, (index) =>
                  m.practice_session_audio_device_microphone({ index }),
                )}
                className='w-full'
                onChange={(event) => chooseMicrophone(event.target.value)}
              />
            </label>
          )}

          <LevelMeter value={level} label={m.meeting_offline_dialog_level()} />

          {failed && (
            <p role='alert' className='text-sm text-danger'>
              {m.meeting_offline_start_failed()}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onCancel}>
            {m.meeting_cancel()}
          </Button>
          <Button size='sm' disabled={pending} onClick={() => onStart(microphoneId)}>
            {m.meeting_offline_start()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
